import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExpandableText } from "@/components/common/ExpandableText";
import { MediaPreview } from "@/components/common/MediaPreview";
import { SendingBadge } from "@/components/common/SendingBadge";
import { UserAvatarWithPopup } from "@/components/common/UserAvatarWithPopup";
import { Card, CardContent } from "@/components/ui/card";
import { CommentEditForm } from "@/features/study-group-stream/components/CommentEditForm";
import { CommentHeaderActions } from "@/features/study-group-stream/components/CommentHeaderActions";
import { InteractionFooter } from "@/features/study-group-stream/components/InteractionFooter";
import {
  getEffectiveIsSending,
  getIsAnyActionLoading,
} from "@/features/study-group-stream/utils/commentUiState";
import { useMentionInputNotice } from "@/hooks/useMentionInputNotice";
import { COMMENT_REACTION_OBJECT_MODEL } from "@/hooks/useCommentReactions";
import type { Comment, ReactionType } from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";
import { convertTimezoneToLocal } from "@/lib/utils/dateUtils";

interface ReplyCardProps {
  author?: string;
  content?: string;
  timestamp?: string;
  avatarUrl?: string | null;
  currentReaction?: ReactionType | null;
  onReaction?: (
    commentId: number,
    reaction: ReactionType | null,
  ) => Promise<void>;
  isReacting?: boolean;
  comment?: Comment;
  isPending?: boolean;
  onReplyClick?: (commentId: number) => void;
  isSending?: boolean;
  onDeleteComment?: (commentId: number) => Promise<void>;
  onEditComment?: (commentId: number, newMessage: string) => Promise<void>;
  isDeleting?: boolean;
  isCurrentUserAuthor?: boolean;
  showReplyButton?: boolean;
  currentUserId?: number | string;
  onSendMessage?: (userId: number) => void;
  isCommentActive?: boolean;
  isHighlighted?: boolean;
  disabled?: boolean;
}

export const ReplyCard: React.FC<ReplyCardProps> = ({
  author,
  content,
  timestamp,
  avatarUrl,
  currentReaction = null,
  onReaction,
  isReacting = false,
  comment,
  isPending = false,
  onReplyClick,
  isSending = false,
  onDeleteComment,
  onEditComment,
  isDeleting = false,
  isCurrentUserAuthor = false,
  showReplyButton = true,
  currentUserId,
  onSendMessage,
  isCommentActive = false,
  isHighlighted = false,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const displayAuthor =
    author ||
    comment?.user?.display_name ||
    comment?.createdBy?.display_name ||
    "";
  const displayContent = content || comment?.message || "";
  const displayTimestamp =
    timestamp || comment?.created_at || comment?.createdAt || "";
  const formattedTimestamp = (() => {
    if (!displayTimestamp) return "";
    const { date, time } = convertTimezoneToLocal(displayTimestamp, "UTC");
    if (!date || !time) return displayTimestamp;
    return `${date} ${time}`;
  })();
  const displayAvatarUrl =
    avatarUrl ||
    comment?.user?.image_url ||
    comment?.createdBy?.image_url ||
    "";
  const displayUserId = comment?.user?.id || comment?.createdBy?.id || 0;
  const displayCommentId = Number(comment?.id ?? 0);
  const hasDisplayCommentId =
    Number.isFinite(displayCommentId) && displayCommentId > 0;
  const displayReaction =
    currentReaction || comment?.reactions?.current_user_emotion || null;
  const effectiveIsSending = getEffectiveIsSending(comment, isSending);

  const [localReaction, setLocalReaction] = useState<ReactionType | null>(
    displayReaction,
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedMessage, setEditedMessage] = useState(displayContent);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const mentionNoticeResetKey = isEditMode
    ? `${displayCommentId}:${displayContent}`
    : null;
  const hasTypedMentionInEdit = useMentionInputNotice(
    editedMessage,
    displayContent,
    mentionNoticeResetKey,
  );

  useEffect(() => {
    setLocalReaction(displayReaction);
  }, [displayReaction]);

  const handleReactionSelect = async (reaction: ReactionType) => {
    if (!hasDisplayCommentId || !onReaction || disabled) return;

    const newReactionState = localReaction === reaction ? null : reaction;
    setLocalReaction(newReactionState);
    try {
      await onReaction(displayCommentId, newReactionState);
    } catch (error) {
      console.error("Reaction failed:", error);
      setLocalReaction(displayReaction);
    }
  };

  const handleReplyButtonClick = () => {
    if (onReplyClick && hasDisplayCommentId && !disabled) {
      onReplyClick(displayCommentId);
    }
  };

  const handleEditClick = (_commentId: number) => {
    setIsEditMode(true);
    setEditedMessage(displayContent);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedMessage(displayContent);
  };

  const handleSubmitEdit = async () => {
    const trimmedMessage = editedMessage.trim();
    if (!onEditComment || trimmedMessage === displayContent.trim()) {
      setIsEditMode(false);
      return;
    }
    if (trimmedMessage === "") {
      alert(t("common.messageCannotBeEmpty"));
      return;
    }
    setIsEditMode(false);
    setIsSubmittingEdit(true);
    try {
      if (!hasDisplayCommentId) return;
      await onEditComment(displayCommentId, trimmedMessage);
    } catch (error) {
      console.error("Failed to edit reply:", error);
      alert(t("common.editError"));
      setEditedMessage(displayContent);
      setIsEditMode(true);
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleDeleteClick = async (commentId: number) => {
    if (!onDeleteComment) {
      console.error("❌ ReplyCard: onDeleteComment handler is missing!");
      return;
    }
    try {
      await onDeleteComment(commentId);
    } catch (error) {
      console.error("❌ ReplyCard: Failed to delete reply:", error);
    }
  };

  const isAnyActionLoading = getIsAnyActionLoading({
    isDeleting,
    isSubmittingEdit,
    isSending: effectiveIsSending,
  });

  return (
    <Card
      data-comment-id={displayCommentId}
      className={cn(
        // ml-8を削除して右端を他カードと揃える
        "flex w-full min-w-0 flex-col rounded-tr-xl border-none shadow-none",
        isHighlighted ? "bg-amber-50" : "stream-bg-reply",
        isPending && "opacity-70",
        effectiveIsSending && "border-l-4 border-blue-500 bg-blue-50",
        isAnyActionLoading && "opacity-70",
      )}
    >
      <CardContent className="flex w-full min-w-0 flex-col gap-2 py-3 pl-3">
        <div className="flex w-full min-w-0 items-start gap-2">
          <UserAvatarWithPopup
            userId={displayUserId}
            userName={displayAuthor}
            avatarUrl={displayAvatarUrl}
            currentUserId={currentUserId}
            onSendMessage={onSendMessage}
            wrapperClassName="h-8 w-8 flex-shrink-0 cursor-pointer transition-opacity hover:opacity-80"
            avatarClassName="h-8 w-8"
            avatarFallbackClassName="text-sm"
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex min-h-8 min-w-0 items-center justify-between">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-semibold text-variable-collection-black-gray">
                  {displayAuthor}
                </span>
                <span className="text-xs font-medium text-rw-dark-gray">
                  {formattedTimestamp}
                </span>
                {effectiveIsSending && <SendingBadge />}
              </div>
              {!disabled && (
                <CommentHeaderActions
                  commentId={displayCommentId ?? 0}
                  isCurrentUserAuthor={isCurrentUserAuthor}
                  isAnyActionLoading={isAnyActionLoading}
                  isDeleting={isDeleting}
                  onEditClick={handleEditClick}
                  onDeleteClick={handleDeleteClick}
                />
              )}
            </div>

            {isEditMode ? (
              <CommentEditForm
                value={editedMessage}
                isSubmitting={isSubmittingEdit}
                onChange={setEditedMessage}
                onSave={handleSubmitEdit}
                onCancel={handleCancelEdit}
                showMentionWarning={hasTypedMentionInEdit}
                cancelButtonVariant="ghost"
              />
            ) : (
              <div className="min-w-0">
                <ExpandableText content={displayContent} />

                {/* Display file attachments if present */}
                {comment?.files && comment.files.length > 0 && (
                  <div className="mt-2">
                    <MediaPreview files={comment.files} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <InteractionFooter
          localReaction={localReaction}
          isReacting={isReacting}
          isPending={isPending || effectiveIsSending}
          commentId={displayCommentId ?? 0}
          onReactionSelect={handleReactionSelect}
          showReplyButton={showReplyButton}
          onReplyClick={handleReplyButtonClick}
          isAnyActionLoading={isAnyActionLoading}
          reactionTotal={comment?.reactions?.total ?? 0}
          lastReactions={comment?.reactions?.last_reactions ?? []}
          reactionBreakdown={comment?.reactions?.by_emotion}
          reactionObjectModel={COMMENT_REACTION_OBJECT_MODEL}
          reactionObjectId={displayCommentId ?? 0}
          isCommentActive={isCommentActive}
          disabled={disabled}
          dividerClassName="border-[#c3ccd1]"
          reactionInactiveIconOpacityClass="opacity-85"
        />
      </CardContent>
    </Card>
  );
};
